import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { DocumentLookupQuerySchema, DocumentLookupResponseSchema, DocumentNeighboursSchema, } from './document-navigation.js';
import { PickerIdsSchema, pagedQuery, pagedResponse } from './pagination.js';
import { AuthorshipSchema, CurrencySchema, MessageSchema, MoneyStringSchema, QtyStringSchema, UnitPrecisionSchema, } from './primitives.js';
const c = initContract();
/**
 * Items (PART 5, MD-03) — the master record every document line, BOM and
 * stock movement will point at. product | service | bundle.
 *
 * Owner rulings carried in the shapes:
 *  - R6: `units` is per-item rows { unitId, factor, allowBuy, allowSell } —
 *    the factor lives HERE, never on the unit vocabulary.
 *  - R5: `prices` is one entry per tier the item is PRICED at, keyed by
 *    tierId. Absence = "no special price" and the sale falls back to the
 *    unit price — never stored as zero (BR-MD-14-04).
 *  - R7: `components` reference REAL item records (componentItemId) with a
 *    qty and one of the COMPONENT's own units; the server stores both the
 *    typed qty and its stock-unit conversion (BR-MD-03-16/-18).
 *
 * Writes carry the child collections whole (replace-set semantics) — the
 * item editor saves one record, not four.
 *
 * Permission area: `inventory` — PART 16.2.2 row 3 ("Items, categories,
 * units of measure, warehouses…"). Matrix 16.4.2: Administrator RCUDA,
 * Accountant R, AP Clerk R.
 *
 * The stock wave closed the items debts: live on-hand/available figures and
 * the per-warehouse table (panel 6 fields 22-25), the derived stock status
 * badge (BR-INV-05-01), the once-moved locks (BR-MD-03-04/-04b/-05) and the
 * stock-aware switch-off (BR-MD-03-11). Still later waves: last-purchase
 * figures, document-line integration (BR-MD-03-14/-22).
 */
// ── The fixed sentences of MD-03 ────────────────────────────────────────────
export const ITEM_MESSAGES = {
    /** ERR-MD-03-01 / BR-MD-03-01. */
    nameRequired: 'Type a name for this item.',
    /** ERR-MD-03-02 / BR-MD-03-02/-03. */
    codeTaken: (code, name) => `${code} is already used by ${name}.`,
    /**
     * ── THE BARCODE'S TWO REFUSALS ────────────────────────────────────────────
     *
     * The specification writes no sentence for either, because it never gives
     * MD-03 a barcode field at all — the column exists because the point of sale
     * cannot work without it (§5c of PART 10: "Matches on barcode FIRST, then on
     * an exact item code, then on any part of the name"), and the migration that
     * added it argues exactly that. So these two sentences are the build's, and
     * are written to the house rules rather than quoted from a row.
     *
     * BOTH ARE FIXED, AND THAT IS A DECISION, NOT A SHORTCUT. `lib/server-messages.ts`
     * maps whole sentences to i18n keys BY EXACT MATCH, so any sentence carrying
     * a value cannot be translated and reaches an Arabic reader in English — the
     * documented fate of `codeTaken` and every other parameterised message here.
     * A duplicate barcode is the one place where that price buys nothing:
     *
     *  - The person typed or scanned the barcode a moment ago. Reading it back
     *    to them names something already on their screen.
     *  - Naming the OTHER item would be worth having, and cannot be done here.
     *    The refusal is raised from the unique index at the instant of the
     *    write, which leaves the transaction aborted; finding the other item's
     *    name would need a second connection, to say something the person can
     *    find by searching the barcode they are holding.
     *
     * So the second clause spends its words on the REASON instead, which is the
     * half a person cannot look up: a barcode is not a label, it is what a
     * scanner resolves, and two items sharing one makes the scan ambiguous.
     */
    barcodeTaken: 'That barcode is already on another item. A scan must find exactly one.',
    /**
     * The length bound, one sentence for both ends. The database says the same
     * thing in `items_barcode_check` (4..60 after trimming), but a CHECK cannot
     * phrase itself, and this field is validated before the write on both sides
     * — so the constraint is the floor, never the messenger.
     */
    barcodeLength: 'A barcode is between 4 and 60 characters.',
    /** ERR-MD-03-04 / BR-MD-03-13. */
    negativePrice: 'A price cannot be below zero.',
    /** ERR-MD-03-05 / BR-MD-03-12. */
    negativeReorder: 'A reorder point cannot be below zero. Use 0 to switch the check off.',
    /** ERR-MD-03-06 / BR-MD-03-10 (document lines arrive with later waves). */
    inUse: (name, lines) => `${name} is used on ${lines} document ${lines === 1 ? 'line' : 'lines'} and cannot be deleted. Switch it off instead.`,
    /** ERR-MD-03-09 (the row's factor left empty). */
    factorMissing: (stockSymbol, unitName) => `Say how many ${stockSymbol} are in one ${unitName}.`,
    /** ERR-MD-03-10 / BR-MD-03-04a. */
    factorZero: 'A conversion factor must be above zero.',
    /** ERR-MD-03-11 / BR-MD-03-16 — a kit's parts are items, not text. */
    kitNeedsRealItem: (line) => `Pick a real item for line ${line}. A kit's parts are items, not text.`,
    /** ERR-MD-03-12 / BR-MD-03-05b. */
    kitInKit: 'A kit cannot contain another kit.',
    /** ERR-MD-03-14 / BR-MD-03-04c. */
    unitDuplicate: (unitName) => `${unitName} is already on this item's unit list.`,
    /** ERR-MD-03-17 / BR-MD-03-19 — refused for switch-off AND delete. */
    partOfKits: (name, kits) => `${name} is a part of ${kits} ${kits === 1 ? 'kit' : 'kits'}. Remove it from them first.`,
    /** BR-MD-03-08 (no spec-fixed wording; the shared inactive pattern). */
    componentInactive: (name) => `${name} is switched off and cannot be picked.`,
    /** MD-03 field 8: components are required when Type is Bundle. */
    kitNeedsComponents: 'A kit needs at least one component.',
    /**
     * INV-08 §12's per-item / per-category inventory mapping: the mapped code
     * must name a postable, active ASSET account — inventory is what the
     * company owns, so a mapping into an expense would misfile the shelves.
     * Shared by the item form and the category editor.
     */
    notAnInventoryAccount: 'No postable, active asset account with this code exists in the chart of accounts.',
    /**
     * MD-03 §9 / OQ-05-14: the See-item-cost gate, on the way IN. Somebody who
     * cannot read the cost must not be able to overwrite it either — a blind
     * write is a worse leak than a read, because it destroys the figure.
     */
    costDenied: 'You do not have permission to see or change an item\'s cost. Ask an administrator to grant it.',
    /** Field 8c: the unit must be one of the component's own units. */
    notAComponentUnit: (unitName, itemName) => `${unitName} is not one of ${itemName}'s units.`,
    /** Field 19a: an item may narrow the vocabulary's permission, never widen it. */
    unitNotAllowed: (unitName, side) => `${unitName} is not allowed on ${side} (MD-12). Allow it on the unit first.`,
    /** Field 16a: above the unit price is allowed AND warns. */
    tierAboveUnit: (tierName) => `The ${tierName} price is above the unit price. A tier is not always a discount — saved.`,
    /**
     * ── BR-MD-05-08 / ERR-MD-05-03, THE ONE MD-05 RULE THAT MEANS SOMETHING
     *    WITHOUT PRICE LISTS ────────────────────────────────────────────────
     *
     * BR-MD-05-08: "A price below the item's cost price must warn: '45.0000 is
     * below the 30.0000 cost of Cotton T-Shirt — Premium. Continue?' and must
     * need a Manager to continue." ERR-MD-05-03 quotes the same sentence, and
     * MD-05 §2 gives the Manager the row "Approves a list that cuts prices
     * below cost — Yes, approval only".
     *
     * The other fifteen MD-05 identifiers are price-LIST rules and stay blocked
     * by the specification (see packages/contracts/src/price-tiers.ts for the
     * three statements that defer them). This one is different in kind: it is
     * about a price and a cost, both of which exist today on MD-03 — the tier
     * price of field 16a and the cost price of field 12 — so it reads perfectly
     * against what is built and was the only one flagged as "NOT built" rather
     * than "meaningless without the record".
     *
     * THE PRECISION OF EACH FIGURE IS ITS OWN FIELD'S. MD-05's example shows
     * both at four decimals because a price-list price is a 4dp field; here the
     * tier price is MD-03 field 16a's money (2dp) and the cost is field 12's
     * cost (4dp, GEN-102). Printing each as the product stores it is what makes
     * the sentence checkable against the screen the user is looking at.
     */
    belowCost: (price, cost, itemName) => `${price} is below the ${cost} cost of ${itemName}. Continue?`,
    /**
     * The same refusal for somebody who may not see the cost (MD-03 §9 /
     * OQ-05-14's See-item-cost gate). The rule still bites — the price is still
     * below cost and still needs a manager — but the sentence must not print
     * the figure, because a refusal that names a number is a way of reading it.
     */
    belowCostHidden: (itemName) => `This price is below the cost of ${itemName}. Continue?`,
    /**
     * BR-MD-05-08's second half: "and must need a Manager to continue."
     * MD-05 §14's own "What the user should do" column for ERR-MD-05-03 is the
     * sentence, verbatim.
     */
    belowCostNeedsManager: 'Ask a manager, or raise the price.',
    /** Duplicate names warn and then save (MD-03 field 2). */
    duplicateName: (name) => `An item named ${name} already exists. Saved anyway — check it is not the same item.`,
    /** BR-MD-03-04 / ERR-MD-03-03 — the STOCK unit is locked once any movement exists. */
    stockUnitLocked: (name, movements, unitSymbol) => `${name} already has ${movements} ${movements === 1 ? 'movement' : 'movements'} in ${unitSymbol}. Create a new item to stock it in a different unit.`,
    /** BR-MD-03-04b / ERR-MD-03-15 — a unit's factor is locked once posted movements used it. */
    factorLocked: (unitName, movements) => `${unitName} is on ${movements} ${movements === 1 ? 'movement' : 'movements'} of this item. Add a new unit instead.`,
    /** BR-MD-03-05 — tracking type locked once any movement exists. */
    trackingLocked: (name, movements) => `${name} already has ${movements} ${movements === 1 ? 'movement' : 'movements'}. Its tracking cannot change — create a new item.`,
    /** BR-MD-03-11 / ERR-MD-03-07 — switch-off refused while stock remains. */
    stockAboveZero: (name, qty, unitSymbol) => `${name} still has ${qty} ${unitSymbol} in stock. Move or write off the stock before switching the item off.`,
    /** MD-RULE-1's stock half: an item with movements is history. */
    hasMovements: (name, movements) => `${name} is on ${movements} stock ${movements === 1 ? 'movement' : 'movements'} and cannot be deleted. Switch it off instead.`,
};
/**
 * D46 — MD-03's structured refusals: key + params beside the composed English
 * sentence, so a refusal that names an item, a unit, a count or a figure
 * reaches an Arabic reader in Arabic. One builder per parameterised member the
 * API raises inside an HTTP exception body — the raise sites throw THESE, and
 * each `message` is composed by the `ITEM_MESSAGES` twin so the two cannot
 * drift (parity pinned member by member in
 * `apps/api/test/refusal-census.spec.ts`; both-language renders in
 * `apps/web/src/test/refusal-wire-render.test.ts`).
 *
 * Two ITEM sentences are deliberately NOT here: `duplicateName` and
 * `tierAboveUnit` ride a 2xx response's warnings field, never a refusal body —
 * the census records them under NOT_REFUSAL_BODIES.
 *
 * `unitNotAllowed`'s `side` is a TOKEN, not prose: the wire carries
 * 'sales' | 'purchases' and each dictionary decides how to say it (en
 * interpolates the token — that is what the English sentence already looks
 * like; ar selects on it). Params stay locale-neutral, which is what lets one
 * body serve both readers.
 */
export const ITEM_REFUSALS = {
    codeTaken: (code, name) => ({
        message: ITEM_MESSAGES.codeTaken(code, name),
        key: 'items.errCodeTaken',
        params: { code, name },
    }),
    inUse: (name, lines) => ({
        message: ITEM_MESSAGES.inUse(name, lines),
        key: 'items.errInUse',
        params: { name, lines },
    }),
    kitNeedsRealItem: (line) => ({
        message: ITEM_MESSAGES.kitNeedsRealItem(line),
        key: 'items.errKitNeedsRealItem',
        params: { line },
    }),
    unitDuplicate: (unitName) => ({
        message: ITEM_MESSAGES.unitDuplicate(unitName),
        key: 'items.errUnitDuplicate',
        params: { unitName },
    }),
    partOfKits: (name, kits) => ({
        message: ITEM_MESSAGES.partOfKits(name, kits),
        key: 'items.errPartOfKits',
        params: { name, kits },
    }),
    componentInactive: (name) => ({
        message: ITEM_MESSAGES.componentInactive(name),
        key: 'items.errComponentInactive',
        params: { name },
    }),
    notAComponentUnit: (unitName, itemName) => ({
        message: ITEM_MESSAGES.notAComponentUnit(unitName, itemName),
        key: 'items.errNotAComponentUnit',
        params: { unitName, itemName },
    }),
    unitNotAllowed: (unitName, side) => ({
        message: ITEM_MESSAGES.unitNotAllowed(unitName, side),
        key: 'items.errUnitNotAllowed',
        params: { unitName, side },
    }),
    belowCost: (price, cost, itemName) => ({
        message: ITEM_MESSAGES.belowCost(price, cost, itemName),
        key: 'items.errBelowCost',
        params: { price, cost, itemName },
    }),
    belowCostHidden: (itemName) => ({
        message: ITEM_MESSAGES.belowCostHidden(itemName),
        key: 'items.errBelowCostHidden',
        params: { itemName },
    }),
    stockUnitLocked: (name, movements, unitSymbol) => ({
        message: ITEM_MESSAGES.stockUnitLocked(name, movements, unitSymbol),
        key: 'items.errStockUnitLocked',
        params: { name, movements, unitSymbol },
    }),
    factorLocked: (unitName, movements) => ({
        message: ITEM_MESSAGES.factorLocked(unitName, movements),
        key: 'items.errFactorLocked',
        params: { unitName, movements },
    }),
    trackingLocked: (name, movements) => ({
        message: ITEM_MESSAGES.trackingLocked(name, movements),
        key: 'items.errTrackingLocked',
        params: { name, movements },
    }),
    stockAboveZero: (name, qty, unitSymbol) => ({
        message: ITEM_MESSAGES.stockAboveZero(name, qty, unitSymbol),
        key: 'items.errStockAboveZero',
        params: { name, qty, unitSymbol },
    }),
    hasMovements: (name, movements) => ({
        message: ITEM_MESSAGES.hasMovements(name, movements),
        key: 'items.errHasMovements',
        params: { name, movements },
    }),
};
// ── Decimal-string primitives of this screen ────────────────────────────────
/** A conversion factor: > 0, up to 6 decimals (MD-03 field 19). */
export const FactorStringSchema = z
    .string()
    .regex(/^\d{1,12}(\.\d{1,6})?$/, ITEM_MESSAGES.factorZero)
    .refine((value) => !/^0+(\.0+)?$/.test(value), ITEM_MESSAGES.factorZero);
/** A non-negative quantity (reorder figures): 0 allowed, up to 3 decimals. */
export const NonNegQtyStringSchema = z
    .string()
    .regex(/^\d{1,12}(\.\d{1,3})?$/, ITEM_MESSAGES.negativeReorder);
/** A cost: non-negative, up to 4 decimals (GEN-102 — cost keeps 4dp). */
export const CostStringSchema = z
    .string()
    .regex(/^\d{1,11}(\.\d{1,4})?$/, ITEM_MESSAGES.negativePrice);
// ── Shapes ──────────────────────────────────────────────────────────────────
export const ItemTypeSchema = z.enum(['product', 'service', 'bundle']);
export const TrackingTypeSchema = z.enum(['none', 'serial', 'batch']);
/**
 * bugs: New Item — IT-S5 "Availability" (plan §12: available / on order /
 * discontinued). A STATEMENT about the item's life, separate from `isActive`
 * (whether it can be picked at all): a discontinued item stays pickable while
 * the shelf still has some.
 */
export const ITEM_AVAILABILITY = ['available', 'on_order', 'discontinued'];
export const ItemAvailabilitySchema = z.enum(ITEM_AVAILABILITY);
/** One row of the item's own unit table (panel 5, R6). */
export const ItemUnitRowSchema = z
    .object({
    unitId: z.uuid(),
    unitName: z.string(),
    unitSymbol: z.string(),
    /** 1 <unit> = <factor> <stock units> — per ITEM (R6). */
    factor: z.string(),
    allowBuy: z.boolean(),
    allowSell: z.boolean(),
})
    .strict();
/** One priced tier (panel 4, R5). Absent tier = no special price. */
export const ItemPriceRowSchema = z
    .object({
    tierId: z.uuid(),
    tierName: z.string(),
    price: z.string(),
})
    .strict();
export const ItemVariantRowSchema = z
    .object({
    sku: z.string(),
    attributes: z.string(),
    /** null = Inherits the parent's unit price (BR-MD-03-05c). */
    priceOverride: z.string().nullable(),
})
    .strict();
/**
 * One label of the item's Barcodes grid (bugs: New Item — IT-N9 "Unit /
 * Barcode / Size / Notes"). The FIRST row is the primary — the same value
 * `barcode` carries, which the till matches first (SAL-06 §5c).
 *
 * `id` is null for exactly one shape: an item whose `items.barcode` predates
 * the `item_barcodes` table and has not been saved since. The read reports it
 * as the one entry so the grid is never empty while the till still scans it;
 * the first save writes it down as a row of its own.
 */
export const ItemBarcodeRowSchema = z
    .object({
    id: z.uuid().nullable(),
    /** null = the stock unit / the label says nothing about the unit. */
    unitId: z.uuid().nullable(),
    unitName: z.string().nullable(),
    barcode: z.string(),
    size: z.string().nullable(),
    notes: z.string().nullable(),
})
    .strict();
/**
 * IT-S1: one warehouse's override of the three reorder figures. Each null =
 * the item's own figure applies at that location. An item with any row is
 * judged warehouse by warehouse; one with none on its totals.
 */
export const ItemWarehouseLevelRowSchema = z
    .object({
    warehouseId: z.uuid(),
    warehouseName: z.string(),
    reorderPoint: z.string().nullable(),
    reorderQty: z.string().nullable(),
    overstockCeiling: z.string().nullable(),
})
    .strict();
/** One kit line (panel 2, R7): a link to a real item record. */
export const KitComponentRowSchema = z
    .object({
    componentItemId: z.uuid(),
    componentCode: z.string(),
    componentName: z.string(),
    /** Field 8d: READ from the component's record — a statement, not a control. */
    componentTracking: TrackingTypeSchema,
    /** Field 8e's ingredient: the component's own cost. Absent with the cost. */
    componentCostPrice: z.string().optional(),
    qty: z.string(),
    unitId: z.uuid(),
    unitName: z.string(),
    unitSymbol: z.string(),
    /** BR-MD-03-18: the same qty in the component's STOCK unit, stored. */
    baseQty: z.string(),
    stockUnitSymbol: z.string(),
})
    .strict();
export const ItemSchema = z
    .object({
    id: z.uuid(),
    /** ITM-#### when Auto allocated it; a hand-typed code otherwise. */
    code: z.string(),
    /** SAL-06 §5c — matched before the code so a scanner lands on the item. */
    barcode: z.string().nullable(),
    itemNo: z.number().int().nullable(),
    name: z.string(),
    /** S5 / IT-C1: the Arabic twin; `localizedName()` picks it on an Arabic screen. */
    nameAr: z.string().nullable(),
    /** IT-C1: the name printed on document lines when set; null = `name`. */
    printName: z.string().nullable(),
    description: z.string().nullable(),
    /** IT-S9: free-text specifications. */
    specifications: z.string().nullable(),
    /** IT-S5. */
    availability: ItemAvailabilitySchema,
    /** IT-S7: false = not a tile on the till (SAL-06 §5c's query filters on it). */
    posVisible: z.boolean(),
    /** IT-S3: what `unitPrice` and the tier prices are IN; null = the base currency. */
    priceCurrency: z.string().nullable(),
    type: ItemTypeSchema,
    categoryId: z.uuid(),
    categoryName: z.string(),
    isActive: z.boolean(),
    stockUnitId: z.uuid(),
    stockUnitName: z.string(),
    stockUnitSymbol: z.string(),
    /**
     * N-4. The STOCK unit is the one that governs every quantity on this
     * record — MD-03 field 17 (R6): "counted, held and valued in this unit".
     * A per-item extra unit (panel 5) has its own precision and its own
     * factor, but nothing is stored in it.
     */
    stockUnitPrecision: UnitPrecisionSchema,
    defaultBuyUnitId: z.uuid().nullable(),
    defaultSellUnitId: z.uuid().nullable(),
    unitPrice: z.string(),
    /**
     * 4dp. ABSENT — not masked — for a caller without `inventory.see_item_cost`
     * (MD-03 §9: "without it the field is not rendered at all"). The read
     * never selects the column, so there is nothing on the wire to reveal.
     */
    costPrice: z.string().optional(),
    /** Field 13, on by default — the on-screen mask, not a permission. */
    hideCost: z.boolean(),
    taxCategory: z.string().nullable(),
    /**
     * INV-08 §12's per-item inventory mapping: the chart account CODE this
     * item's stock value posts to. NULL = the category's mapping, else 1131.
     */
    inventoryAccountCode: z.string().nullable(),
    multiTier: z.boolean(),
    autoDeduct: z.boolean(),
    trackingType: TrackingTypeSchema,
    batchNo: z.string().nullable(),
    expiryDate: z.string().nullable(),
    reorderPoint: z.string(),
    reorderQty: z.string(),
    /** PART 8 §8.1: the Overstock line, measured on ON-HAND; null = off. */
    overstockCeiling: z.string().nullable(),
    /**
     * bugs: Items — "some items' stock doesn't matter". OFF takes the item
     * out of the Low/Out KPI counts and silences its stock-status badge
     * (`stockStatus` reads 'ok'); the figures themselves are still shown.
     */
    trackStockAlerts: z.boolean(),
    preferredSupplierId: z.uuid().nullable(),
    supplierItemCode: z.string().nullable(),
    leadTimeDays: z.number().int().nullable(),
    /** IT-S6: who makes it (manufacturers master); null = not stated. */
    manufacturerId: z.uuid().nullable(),
    manufacturerName: z.string().nullable(),
    /**
     * IT-N4: set = soft-deleted — out of every list, picker, search and KPI by
     * default (the Status filter "Deleted" and the `ids` read still reach it);
     * restorable. null = an ordinary item.
     */
    deletedAt: z.string().nullable(),
    /** MD-03 panel 6 fields 22–25: LIVE figures from stock_levels; [] for a Service. */
    stock: z
        .object({
        warehouseId: z.uuid(),
        warehouseName: z.string(),
        /** IT-L4: the warehouse's #rrggbb, for the catalogue's per-location chips; null = none. */
        warehouseColour: z.string().nullable(),
        onHand: z.string(),
        reserved: z.string(),
        /** on hand − reserved, computed (BR-INV-05-04). */
        available: z.string(),
    })
        .strict()
        .array(),
    totalOnHand: z.string(),
    totalAvailable: z.string(),
    /**
     * IT-S2: the weighted-average unit cost of what is on the shelves, across
     * every warehouse (Σ stock value ÷ Σ on hand, 4dp; '0' when nothing is
     * held). READ-ONLY — the movement writer maintains it. ABSENT for a caller
     * without `inventory.see_item_cost`, like `costPrice`.
     */
    currentCost: z.string().optional(),
    /** IT-S1: the per-warehouse overrides; [] = the item's figures everywhere. */
    warehouseLevels: ItemWarehouseLevelRowSchema.array(),
    /** out → low(available) → over(on-hand) → ok (BR-INV-05-01) — the catalog badge. */
    stockStatus: z.enum(['ok', 'low', 'out', 'over']),
    /** True once any movement exists — drives the once-moved locks (BR-MD-03-04/-05). */
    hasMovements: z.boolean(),
    units: ItemUnitRowSchema.array(),
    prices: ItemPriceRowSchema.array(),
    variants: ItemVariantRowSchema.array(),
    components: KitComponentRowSchema.array(),
    /** IT-N9: every label, first = primary (`barcode`). See ItemBarcodeRowSchema. */
    barcodes: ItemBarcodeRowSchema.array(),
    /** "· 3 variants" in the catalog (MD-03 §4a). */
    variantCount: z.number().int(),
    /**
     * IT-L5: the NEWEST image/* attachment on the item (INV-H5), for the
     * catalogue's row thumbnail (`/api/attachments/:id/content`). null = no
     * picture yet, and the list draws its placeholder.
     */
    thumbnailAttachmentId: z.uuid().nullable(),
    // MD-RULE-5's six fields, `createdAt` among them (it was here alone rule-coverage:ignore
    // until 20260902100000 gave the table the author columns).
    ...AuthorshipSchema.shape,
})
    .strict();
const ItemUnitInputSchema = z
    .object({
    unitId: z.uuid(),
    factor: FactorStringSchema,
    allowBuy: z.boolean().default(true),
    allowSell: z.boolean().default(true),
})
    .strict();
const ItemPriceInputSchema = z
    .object({
    tierId: z.uuid(),
    /** >= 0 (ERR-MD-03-04); above the unit price warns, never refuses. */
    price: MoneyStringSchema,
})
    .strict();
const ItemVariantInputSchema = z
    .object({
    sku: z.string().trim().min(1).max(60),
    attributes: z.string().trim().min(1).max(120),
    priceOverride: MoneyStringSchema.nullable().optional(),
})
    .strict();
const KitComponentInputSchema = z
    .object({
    componentItemId: z.uuid(),
    /** > 0 (field 8b); decimals bounded by the unit's precision (server). */
    qty: QtyStringSchema,
    /** One of the COMPONENT's own units; omitted = its stock unit. */
    unitId: z.uuid().optional(),
})
    .strict();
/**
 * IT-N9's grid row. `id` is accepted and ignored: the list is a replace-set
 * like `units`, so the editor may echo rows back whole without stripping it.
 * `unitId` must be the stock unit or one of the item's own units (the server
 * refuses with ERR-MD-03's not-a-unit-of sentence); null = no unit stated.
 */
const ItemBarcodeInputSchema = z
    .object({
    id: z.uuid().optional(),
    unitId: z.uuid().nullable().optional(),
    barcode: z
        .string()
        .trim()
        .min(4, ITEM_MESSAGES.barcodeLength)
        .max(60, ITEM_MESSAGES.barcodeLength),
    size: z.string().trim().max(40).nullable().optional(),
    notes: z.string().trim().max(200).nullable().optional(),
})
    .strict();
/** IT-S1's row. Absent or null figure = the item's own applies there. */
const ItemWarehouseLevelInputSchema = z
    .object({
    warehouseId: z.uuid(),
    reorderPoint: NonNegQtyStringSchema.nullable().optional(),
    reorderQty: NonNegQtyStringSchema.nullable().optional(),
    overstockCeiling: QtyStringSchema.nullable().optional(),
})
    .strict();
const ItemChildrenSchemas = {
    units: ItemUnitInputSchema.array().max(50).default([]),
    /** IT-S1: replace-set; a row with every figure null is dropped. */
    warehouseLevels: ItemWarehouseLevelInputSchema.array().max(200).default([]),
    prices: ItemPriceInputSchema.array().max(200).default([]),
    variants: ItemVariantInputSchema.array().max(200).default([]),
    components: KitComponentInputSchema.array().max(200).default([]),
    /**
     * Omitted or empty = fall back to the legacy `barcode` field (the CSV import
     * still speaks it): a `barcode` alone is `barcodes: [{ barcode }]`. When
     * both are sent the list wins and the first row becomes the primary.
     */
    barcodes: ItemBarcodeInputSchema.array().max(50).default([]),
};
export const CreateItemRequestSchema = z
    .object({
    /** Omitted = the Auto switch ON: the server allocates ITM-#### on save. */
    code: z.string().trim().min(1).max(40).optional(),
    /**
     * Omitted = this item is not scanned. `.trim()` runs BEFORE the bounds
     * (verified against zod 4.4.3, not assumed from the reading order), so a
     * box holding only spaces is refused here rather than reaching the
     * database's own CHECK — which is why the service needs no handler for
     * `items_barcode_check`.
     *
     * The version is provenance; the guarantee is an assertion elsewhere that
     * an all-spaces barcode is answered 400 and never 500. A number in a
     * comment stops being checked the moment the dependency moves.
     */
    barcode: z
        .string()
        .trim()
        .min(4, ITEM_MESSAGES.barcodeLength)
        .max(60, ITEM_MESSAGES.barcodeLength)
        .optional(),
    name: z.string().trim().min(1, ITEM_MESSAGES.nameRequired).max(200),
    nameAr: z.string().trim().min(1).max(200).optional(),
    printName: z.string().trim().min(1).max(200).optional(),
    description: z.string().trim().max(1000).optional(),
    specifications: z.string().trim().max(4000).optional(),
    availability: ItemAvailabilitySchema.optional(),
    /** Omitted = shown on the till (the column's default). */
    posVisible: z.boolean().optional(),
    /** One of the tenant's ACTIVE currencies (currency-guard); omitted = base. */
    priceCurrency: CurrencySchema.optional(),
    type: ItemTypeSchema.default('product'),
    categoryId: z.uuid(),
    stockUnitId: z.uuid(),
    defaultBuyUnitId: z.uuid().optional(),
    defaultSellUnitId: z.uuid().optional(),
    unitPrice: MoneyStringSchema.optional(),
    costPrice: CostStringSchema.optional(),
    hideCost: z.boolean().optional(),
    taxCategory: z.string().trim().max(60).optional(),
    inventoryAccountCode: z.string().trim().min(1).max(20).optional(),
    multiTier: z.boolean().optional(),
    autoDeduct: z.boolean().optional(),
    trackingType: TrackingTypeSchema.optional(),
    batchNo: z.string().trim().max(40).optional(),
    expiryDate: z.iso.date().optional(),
    reorderPoint: NonNegQtyStringSchema.optional(),
    reorderQty: NonNegQtyStringSchema.optional(),
    overstockCeiling: QtyStringSchema.optional(),
    /** Omitted = tracked (the column's default). */
    trackStockAlerts: z.boolean().optional(),
    /**
     * BR-MD-05-08's "Continue?" answered yes. Absent or false is the ordinary
     * save; true means the caller has SEEN the below-cost question and is
     * proceeding, which the server only accepts from somebody holding the
     * Manager approval MD-05 §2 names. It is not a permission claim — the
     * permission is read server-side — it is the confirmation, which is the
     * half a permission check cannot supply.
     */
    confirmBelowCost: z.boolean().optional(),
    preferredSupplierId: z.uuid().optional(),
    supplierItemCode: z.string().trim().max(40).optional(),
    leadTimeDays: z.number().int().min(0).max(365).optional(),
    /** IT-S6: an ACTIVE maker (a switched-off one is refused on a new item). */
    manufacturerId: z.uuid().optional(),
    ...ItemChildrenSchemas,
})
    .strict();
export const UpdateItemRequestSchema = z
    .object({
    code: z.string().trim().min(1).max(40).optional(),
    /**
     * NULLABLE HERE AND NOT ON CREATE, which is the same asymmetry every other
     * clearable field on this schema carries (`taxCategory`, `supplierItemCode`,
     * `overstockCeiling`): absent means "leave it alone", `null` means "take it
     * off". A barcode that was typed wrong, or a label that was reprinted, has
     * to be removable — and while it is wrong it is occupying a value some
     * other item may legitimately need, since `items_barcode_key` is unique per
     * tenant.
     */
    barcode: z
        .string()
        .trim()
        .min(4, ITEM_MESSAGES.barcodeLength)
        .max(60, ITEM_MESSAGES.barcodeLength)
        .nullable()
        .optional(),
    name: z.string().trim().min(1, ITEM_MESSAGES.nameRequired).max(200).optional(),
    nameAr: z.string().trim().min(1).max(200).nullable().optional(),
    printName: z.string().trim().min(1).max(200).nullable().optional(),
    description: z.string().trim().max(1000).nullable().optional(),
    specifications: z.string().trim().max(4000).nullable().optional(),
    availability: ItemAvailabilitySchema.optional(),
    posVisible: z.boolean().optional(),
    /** null = back to the base currency. */
    priceCurrency: CurrencySchema.nullable().optional(),
    type: ItemTypeSchema.optional(),
    categoryId: z.uuid().optional(),
    stockUnitId: z.uuid().optional(),
    defaultBuyUnitId: z.uuid().nullable().optional(),
    defaultSellUnitId: z.uuid().nullable().optional(),
    unitPrice: MoneyStringSchema.optional(),
    costPrice: CostStringSchema.optional(),
    hideCost: z.boolean().optional(),
    taxCategory: z.string().trim().max(60).nullable().optional(),
    inventoryAccountCode: z.string().trim().min(1).max(20).nullable().optional(),
    multiTier: z.boolean().optional(),
    autoDeduct: z.boolean().optional(),
    /** BR-MD-05-08's "Continue?" answered yes — see CreateItemRequestSchema. */
    confirmBelowCost: z.boolean().optional(),
    trackingType: TrackingTypeSchema.optional(),
    batchNo: z.string().trim().max(40).nullable().optional(),
    expiryDate: z.iso.date().nullable().optional(),
    reorderPoint: NonNegQtyStringSchema.optional(),
    reorderQty: NonNegQtyStringSchema.optional(),
    overstockCeiling: QtyStringSchema.nullable().optional(),
    trackStockAlerts: z.boolean().optional(),
    preferredSupplierId: z.uuid().nullable().optional(),
    supplierItemCode: z.string().trim().max(40).nullable().optional(),
    leadTimeDays: z.number().int().min(0).max(365).nullable().optional(),
    /** null = no maker stated; the stored maker survives a re-save even when switched off. */
    manufacturerId: z.uuid().nullable().optional(),
    /** Child collections REPLACE the stored set when provided. */
    units: ItemUnitInputSchema.array().max(50).optional(),
    prices: ItemPriceInputSchema.array().max(200).optional(),
    variants: ItemVariantInputSchema.array().max(200).optional(),
    components: KitComponentInputSchema.array().max(200).optional(),
    /**
     * Replace-set too. Absent AND `barcode` absent = the labels are left
     * alone; a legacy `barcode` (string or null) alone rewrites the list to
     * that one label or to none — the primary and the list never disagree.
     */
    barcodes: ItemBarcodeInputSchema.array().max(50).optional(),
    /** IT-S1: replace-set when provided. */
    warehouseLevels: ItemWarehouseLevelInputSchema.array().max(200).optional(),
})
    .strict();
/** 201/200 carry the record PLUS non-blocking warnings (duplicate name, tier above unit price). */
export const ItemWriteResponseSchema = z
    .object({
    item: ItemSchema,
    warnings: z.string().array(),
})
    .strict();
const itemParams = z.object({ itemId: z.uuid() }).strict();
/**
 * bugs: Items — "sort by name / category / type / price / qty / last
 * modified". The catalogue's sortable columns, as a closed list: the server
 * maps each key to a whitelisted ORDER BY expression and nothing else reaches
 * the SQL. Absent = the historical code order.
 */
export const ITEM_SORT_KEYS = ['name', 'category', 'type', 'price', 'onHand', 'updatedAt'];
export const ItemSortKeySchema = z.enum(ITEM_SORT_KEYS);
export const SortDirSchema = z.enum(['asc', 'desc']);
export const itemsContract = c.router({
    list: {
        method: 'GET',
        path: '/api/items',
        query: pagedQuery({
            /**
             * Matches code, name or category name, ignoring capitals (§4a) — and,
             * since INV-L5: any of the item's barcodes by prefix (a scanner sends
             * the whole code; a person types the first digits), a variant SKU, and
             * the name through `ar_norm` so أنور finds انور (G-06) — and a SERIAL
             * NUMBER, exactly and ignoring case: a serial names one unit, so the
             * item that unit belongs to is the answer (the ledger's
             * `stock_movements.serial_no` is the registry, there being no other).
             */
            q: z.string().optional(),
            type: ItemTypeSchema.optional(),
            /**
             * bugs: Items — a category filter INCLUDES its descendant categories
             * (Apparel shows Apparel › Men's too). Resolved server-side, so the
             * tree walk is one recursive query rather than a browser fold.
             */
            categoryId: z.uuid().optional(),
            /** IT-S6: items of one maker. */
            manufacturerId: z.uuid().optional(),
            /**
             * The catalog's Status filter defaults to ALL statuses (§4a) — of the
             * items that are not deleted. `deleted` (IT-N4) is the only way a
             * soft-deleted item is listed; absent/active/inactive all exclude it.
             */
            status: z.enum(['active', 'inactive', 'deleted']).optional(),
            /**
             * bugs: Items — Tracked / Untracked / All on `trackStockAlerts`.
             * Absent = all.
             */
            alerts: z.enum(['tracked', 'untracked']).optional(),
            /** See ITEM_SORT_KEYS. Absent = code order; `dir` defaults to asc. */
            sort: ItemSortKeySchema.optional(),
            dir: SortDirSchema.optional(),
            /**
             * BR-MD-03-07 + MD-03 field 7, as ONE predicate: an item a stock
             * document can move. A service has no quantity, and an auto-deducting
             * kit is assembled when it is sold and never sits on a shelf — so
             * neither can appear on a voucher or a transfer.
             *
             * It is here rather than in the two screens that need it because a
             * picker narrowed AFTER its page arrives shows four rows and calls
             * them the whole catalogue.
             */
            stockable: z.enum(['true', 'false']).optional(),
            /**
             * BR-MD-03-05b — "a kit cannot contain another kit". The parts a
             * bundle may reference, as a predicate rather than a fold over a page.
             */
            kitComponent: z.enum(['true', 'false']).optional(),
            /**
             * …and the other half of the same picker: an item is never a part of
             * itself (BR-MD-03-16's shape, and BR-MFG-01-04's on the recipe side).
             */
            excludeId: z.uuid().optional(),
            /**
             * GEN-50's second read: the items a document's saved lines already
             * name. It IGNORES every other filter — including `status` — because
             * a line written last year must still display its item after the item
             * was switched off. See the picker convention in `pagination.ts`.
             */
            ids: PickerIdsSchema.optional(),
        }),
        responses: {
            200: pagedResponse('items', ItemSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The catalog (inventory.view), FILTERED IN SQL AND PAGED (NFR-42): search + category/type/status narrow together, ordered by code, total after filtering. `pageSize: "all"` is the picker escape — see UNPAGED_CALLERS.',
    },
    /**
     * MD-03 §4a's KPI strip — "counting the WHOLE catalog, never the filtered
     * view", which is why it cannot be derived from a page.
     *
     * Before NFR-42 the screen got these four numbers by fetching the entire
     * catalogue A SECOND TIME and counting in JavaScript, so paging the list
     * would have halved a 51 MB screen and left it at 25 MB. Four `count(*)`s
     * in one statement instead — the arithmetic belongs where the rows are.
     */
    stats: {
        method: 'GET',
        path: '/api/items/stats',
        responses: {
            200: z
                .object({
                total: z.number().int().min(0),
                active: z.number().int().min(0),
                /** BR-INV-05-01/-02: low is measured on AVAILABLE, out on ON HAND. */
                lowStock: z.number().int().min(0),
                outOfStock: z.number().int().min(0),
            })
                .strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'MD-03 §4a KPI strip (inventory.view): whole-catalog counts, computed in SQL.',
    },
    /**
     * S4 — the document header's ‹‹ ›› rail: the items on either side of
     * this one in code order (null at an end). See `document-navigation.ts`.
     */
    neighbours: {
        method: 'GET',
        path: '/api/items/:itemId/neighbours',
        pathParams: z.object({ itemId: z.uuid() }).strict(),
        responses: {
            200: DocumentNeighboursSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'The previous and next item by code (inventory.view).',
    },
    /** S4 — the "Go to no." box: one item by its printed code; 404 on a miss. */
    lookup: {
        method: 'GET',
        path: '/api/items/lookup',
        query: DocumentLookupQuerySchema,
        responses: {
            200: DocumentLookupResponseSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One item by its printed code (inventory.view).',
    },
    get: {
        method: 'GET',
        path: '/api/items/:itemId',
        pathParams: itemParams,
        responses: { 200: ItemSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'One item with its units, tier prices, variants and kit lines (inventory.view).',
    },
    create: {
        method: 'POST',
        path: '/api/items',
        body: CreateItemRequestSchema,
        responses: {
            201: ItemWriteResponseSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New item (inventory.create). Code allocated on save when Auto is on (BR-MD-03-01); hand-typed duplicates refused (BR-MD-03-03); duplicate names warn and save.',
    },
    update: {
        method: 'PATCH',
        path: '/api/items/:itemId',
        pathParams: itemParams,
        body: UpdateItemRequestSchema,
        responses: {
            200: ItemWriteResponseSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit an item (inventory.edit). Child collections replace when provided. Once any movement exists the stock unit, unit factors and tracking type are locked (BR-MD-03-04/-04b/-05).',
    },
    deactivate: {
        method: 'POST',
        path: '/api/items/:itemId/deactivate',
        pathParams: itemParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: ItemSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Switch off (inventory.edit). Refused while any kit lists it (BR-MD-03-19) and while stock is above zero (BR-MD-03-11).',
    },
    reactivate: {
        method: 'POST',
        path: '/api/items/:itemId/reactivate',
        pathParams: itemParams,
        body: z.object({}).strict().optional(),
        responses: { 200: ItemSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'Switch back on (inventory.edit).',
    },
    /**
     * IT-N4 — the soft delete: the item leaves every list, picker, search and
     * KPI, and nothing is removed, so `restore` brings it back whole. Refused
     * while stock is held (on hand ≠ 0 — BR-MD-03-11's sentence) and while a
     * kit lists it (BR-MD-03-19). The hard `remove` below stays MD-RULE-1's.
     */
    softDelete: {
        method: 'POST',
        path: '/api/items/:itemId/delete',
        pathParams: itemParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: ItemSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Soft-delete (inventory.delete): hidden everywhere by default, restorable. Refused while stock is held (BR-MD-03-11) or a kit lists it (BR-MD-03-19).',
    },
    restore: {
        method: 'POST',
        path: '/api/items/:itemId/restore',
        pathParams: itemParams,
        body: z.object({}).strict().optional(),
        responses: { 200: ItemSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'Undo a soft delete (inventory.edit): the item is back in every list as it was.',
    },
    remove: {
        method: 'DELETE',
        path: '/api/items/:itemId',
        pathParams: itemParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete (inventory.delete) — only an item used NOWHERE (MD-RULE-1/BR-MD-03-10); a kit component is refused with ERR-MD-03-17.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=items.js.map